// NIGHT-DIRECTSTAFF-2026-10-22 §6: offline lists and the "Sending when online" queue.
//
// THE LISTS. The installed app keeps the last-loaded Today, Needs a human, Leads and Facilities lists so they can be read
// with no connection, clearly marked "Offline — last updated <time>". What is kept is the DISPLAY ROWS -- the words the
// lists already show (a name, what they asked, where they came from, an email where the list shows one) -- never a phone
// number, a raw form payload or a token. One snapshot per signed-in user, replaced on every load, cleared on sign-out.
//
// THE QUEUE. A text or an email written while offline is kept (on this device only) and shown as "Sending when online";
// when the connection comes back each one is sent ONCE -- taken off the queue before it is sent, so a crash mid-send never
// sends it twice -- and the outcome is said. The queue is cleared on sign-out as well.
// Agents window, 10-22: a send queued MORE THAN 30 MINUTES before the connection came back is never sent on its own -- the
// conversation may have moved on -- it waits as "Send this now?" (Send now / Cancel). Every queued send can be cancelled.
export const SNAPSHOT_KEY = 'crm.offline.lists.v1';
export const OUTBOX_KEY = 'crm.offline.outbox.v1';
const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const PHONE = /\+?\(?\d[\d\s().-]{6,}\d/g;
// a number typed into words is masked; a calendar date (2026-11-19) is not a phone number (as sw.js's mask)
const noPhone = (v) => s(v).replace(PHONE, (m) => (/^\d{4}-\d{2}-\d{2}$/.test(m.trim()) || m.replace(/\D/g, '').length < 7 ? m : '•••'));
const safe = (fn, fallback) => { try { return fn(); } catch { return fallback; } };

/** The display rows to keep -- from what the views already computed. Every field is words a list already shows. */
export function snapshotOf({ today = null, needs = [], leads = [], facilities = [], user = '', now = Date.now() } = {}) {
  const t = today || { facility: [], calls: [], handoffs: [], reminders: [], stuck: [], ending: [] };
  const item = (name, line) => ({ name: noPhone(name), line: noPhone(line) });
  return {
    v: 1, user: s(user), savedAt: new Date(now).toISOString(),
    today: {
      total: Number(t.total) || 0,
      sections: [
        ['Facility requests waiting', (t.facility || []).map((r) => item(r.lead && r.lead.name ? r.lead.name : 'Facility request', `${r.hours || 0} h since it came in`))],
        ['Call requested', (t.calls || []).map((r) => item((r.lead && r.lead.name) || 'No name given', `${r.hours || 0} h ago`))],
        ['Hand-offs waiting over 24 hours', (t.handoffs || []).map((h) => item((h.lead && h.lead.name) || 'A conversation', `${h.hours} h, no staff reply yet`))],
        ['Exam reminders due', (t.reminders || []).map((x) => item(x.candidate && x.candidate.name, (x.names || []).join(', ')))],
        ['Onboarding stuck over 3 days', (t.stuck || []).map((r) => item(r.name, r.stepText))],
        ['Placements ending soon', (t.ending || []).map((x) => item(x.candidate && x.candidate.name, `${s(x.placement && x.placement.facility)} · ends ${s(x.placement && x.placement.end_date)} · ${s(x.statusText)}`))],
      ].map(([title, items]) => ({ title, items })),
    },
    needs: (needs || []).map((r) => ({ name: noPhone(r.name || 'No name given'), source: noPhone(r.source), asked: noPhone(r.asked && r.asked.text).slice(0, 200), email: s(r.email) })),
    leads: (leads || []).map((l) => ({ name: noPhone(l.name || l.email || 'No name'), email: s(l.email), source: s(l.source_site), discipline: s(l.discipline), state: s(l.state),
      at: s(l.created_at), handled: !!l.processed })),
    facilities: (facilities || []).map((f) => ({ facility: noPhone(f.facility), contact: noPhone(f.contact), need: noPhone(f.need).slice(0, 200), discipline: s(f.discipline),
      place: noPhone(f.cityState), start: s(f.start), status: s(f.statusLabel || f.status), at: s(f.receivedAt) })),
  };
}

export function saveSnapshot(storage, snap) {
  return safe(() => { storage.setItem(SNAPSHOT_KEY, JSON.stringify(snap)); return true; }, false);
}
/** The snapshot for this user, or null (none, unreadable, or someone else's). */
export function readSnapshot(storage, user = '') {
  const x = safe(() => JSON.parse(storage.getItem(SNAPSHOT_KEY) || 'null'), null);
  return x && x.v === 1 && (!user || x.user === s(user)) ? x : null;
}
export const offlineLabel = (snap) => `Offline — last updated ${snap && snap.savedAt
  ? new Date(snap.savedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'never'}`;

// ---- the queue -----------------------------------------------------------------------------------------------------
export function readOutbox(storage) { return safe(() => JSON.parse(storage.getItem(OUTBOX_KEY) || '[]'), []) || []; }
function writeOutbox(storage, list) { safe(() => storage.setItem(OUTBOX_KEY, JSON.stringify(list)), null); }
/** item: { kind: 'text' | 'email', payload } -> the queued item */
export function enqueue(storage, { kind, payload }, now = Date.now()) {
  const it = { id: `${now.toString(36)}-${Math.random().toString(36).slice(2, 8)}`, kind, payload, queuedAt: new Date(now).toISOString() };
  writeOutbox(storage, [...readOutbox(storage), it]);
  return it;
}
export const STALE_MS = 30 * 60e3;
export const isStale = (it, now = Date.now(), staleMs = STALE_MS) => !!it && now - Date.parse(it.queuedAt) > staleMs;
/** Take one item off the queue (Cancel). -> true if it was there */
export function removeQueued(storage, id) {
  const list = readOutbox(storage);
  if (!list.some((x) => x.id === id)) return false;
  writeOutbox(storage, list.filter((x) => x.id !== id));
  return true;
}
/** Send ONE queued item now ("Send this now?" -> Send now): off the queue first, then sent. -> { id, kind, ok, error } | null */
export async function sendQueued(storage, id, send) {
  const it = readOutbox(storage).find((x) => x.id === id);
  if (!it || !removeQueued(storage, id)) return null;
  try { await send(it); return { id, kind: it.kind, ok: true }; } catch (e) { return { id, kind: it.kind, ok: false, error: (e && e.message) || 'Not sent' }; }
}
/**
 * Send what is queued, once each, in order -- only the FRESH ones (queued within 30 minutes); stale ones stay, waiting for
 * "Send this now?". send(item) -> Promise. Each item is taken off the queue BEFORE its send.
 * -> [{ id, kind, ok, error }]
 */
export async function flushOutbox(storage, send, { now = Date.now(), staleMs = STALE_MS } = {}) {
  const out = [];
  for (;;) {
    const next = readOutbox(storage).find((x) => !isStale(x, now, staleMs));
    if (!next) break;
    const r = await sendQueued(storage, next.id, send);
    if (r) out.push(r);
  }
  return out;
}
/** Sign-out: the lists and the queue go. */
export function clearOffline(storage) {
  safe(() => { storage.removeItem(SNAPSHOT_KEY); storage.removeItem(OUTBOX_KEY); }, null);
}
export const isOffline = (nav = typeof navigator !== 'undefined' ? navigator : null) => !!nav && nav.onLine === false;
